// Message Types for AI Chat
// Each message has a specific type and structure

export const MessageType = {
  USER: 'user',
  ASSISTANT: 'assistant',
  TOOL_CALL: 'tool_call',
  TOOL_RESULT: 'tool_result',
  TOOL_GROUP: 'tool_group',
  SYSTEM: 'system',
  ACTION: 'action'
};

export const MessageStatus = {
  NORMAL: 'normal',
  ERROR: 'error'
};

// Message Factories

export function createUserMessage(content, options = {}) {
  return {
    type: MessageType.USER,
    content,
    displayContent: options.displayContent || null,
    // History outlives the object URLs and must never hold base64, so only the
    // hosted URL and the name survive here.
    attachments: (options.attachments || []).map(({ url, name }) => ({ url, name })),
    timestamp: Date.now()
  };
}

export function createAssistantMessage(content, options = {}) {
  return {
    type: MessageType.ASSISTANT,
    content,
    status: options.error ? MessageStatus.ERROR : MessageStatus.NORMAL,
    canRegenerate: options.canRegenerate || false,
    originalMessage: options.originalMessage || null,
    hidden: options.hidden || false,
    timestamp: Date.now()
  };
}

function parseToolArguments(raw) {
  try {
    return JSON.parse(raw);
  } catch {
    return {};
  }
}

export function createToolCallMessage(toolCalls) {
  return {
    type: MessageType.TOOL_CALL,
    toolCalls: toolCalls.map(tc => ({
      id: tc.id,
      name: tc.function.name,
      arguments: parseToolArguments(tc.function.arguments)
    })),
    timestamp: Date.now()
  };
}

export function createToolResultMessage(toolCallId, name, result, displayText) {
  return {
    type: MessageType.TOOL_RESULT,
    toolCallId,
    name,
    result,
    displayText,
    timestamp: Date.now()
  };
}

export function createSystemMessage(content) {
  return {
    type: MessageType.SYSTEM,
    content,
    timestamp: Date.now()
  };
}

export function createActionMessage(content, originalMessage) {
  return {
    type: MessageType.ACTION,
    content,
    originalMessage,
    timestamp: Date.now()
  };
}

// Filters

export function filterForUI(messages) {
  return messages.filter(msg => {
    if (msg.hidden) return false;
    if (msg.type === MessageType.TOOL_CALL) return false;
    if (msg.type === MessageType.TOOL_RESULT && !msg.displayText) return false;
    return true;
  });
}

// Consecutive tool results become one collapsible row in the UI
export function groupToolResults(messages) {
  const items = [];
  for (const msg of messages) {
    const last = items[items.length - 1];
    if (msg.type !== MessageType.TOOL_RESULT) {
      items.push(msg);
    } else if (last?.type === MessageType.TOOL_GROUP) {
      last.items.push(msg);
    } else {
      items.push({ type: MessageType.TOOL_GROUP, items: [msg] });
    }
  }
  return items;
}

export function filterForAPI(messages) {
  return messages
    .filter(msg => {
      if (msg.type === MessageType.SYSTEM) return false;
      if (msg.type === MessageType.ACTION) return false;
      return true;
    })
    .map(msg => {
      switch (msg.type) {
        case MessageType.USER:
          return { role: 'user', content: msg.content };

        case MessageType.ASSISTANT:
          return { role: 'assistant', content: msg.content };

        case MessageType.TOOL_CALL:
          return {
            role: 'assistant',
            content: null,
            tool_calls: msg.toolCalls.map(tc => ({
              id: tc.id,
              type: 'function',
              function: {
                name: tc.name,
                arguments: JSON.stringify(tc.arguments)
              }
            }))
          };

        case MessageType.TOOL_RESULT:
          return {
            role: 'tool',
            tool_call_id: msg.toolCallId,
            content: typeof msg.result === 'string' ? msg.result : JSON.stringify(msg.result)
          };

        default:
          return null;
      }
    })
    .filter(Boolean);
}

